import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Spinner, Table, ClampText } from '@/components/ui';
import ServerPagination from '../../../components/ServerPagination';
import {
  getScheduleResources,
  getScheduleVehicles,
  type ScheduleResource,
  type ScheduleVehicle,
} from '../../../services/scheduleService';
import { getScheduleTableDateRange } from '../utils/scheduleTableDateRange';
import {
  buildVehicleResources,
  filterResourcesByTaskFilters,
  filterResourcesByTaskSearch,
  parseServiceAreaFilterIds,
  toScheduleListParam,
  type ScheduleResourcePageFilters,
} from '../utils/scheduleResourceAggregation';
import { compareLocale, compareNullableTime, dateStringToSortTime } from '../utils/scheduleTableSort';
import { formatDate } from '@/utils/displayDateTime';

export interface ScheduleEquipmentTableProps {
  filters: ScheduleResourcePageFilters;
  foremanAreas: Array<{ id: number; name: string }>;
  projects: Array<{ id: number; name: string; projectNumber: string }>;
}

interface EquipmentRow {
  equipmentType: string;
  serviceAreaCapacity: string;
  taskId: number;
  taskNumber: string;
  taskName: string;
  projectLabel: string;
  foremanAreaName: string;
  plannedStartRaw: string;
  plannedEndRaw: string;
  status: string;
  yearRound: boolean;
}

type EquipmentSortKey =
  | 'equipmentType'
  | 'serviceAreaCapacity'
  | 'taskNumber'
  | 'taskName'
  | 'projectLabel'
  | 'foremanAreaName'
  | 'plannedStart'
  | 'plannedEnd'
  | 'status'
  | 'yearRound';

function formatScheduleDateCell(iso: string | null | undefined): string {
  if (!iso || !String(iso).trim()) return '–';
  const raw = String(iso).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return formatDate(d);
  }
  const t = Date.parse(raw);
  return Number.isFinite(t) ? formatDate(t) : raw;
}

const ScheduleEquipmentTable: React.FC<ScheduleEquipmentTableProps> = ({ filters, foremanAreas, projects }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<'forbidden' | 'error' | null>(null);
  const [rawResources, setRawResources] = useState<ScheduleResource[] | null>(null);
  const [sortKey, setSortKey] = useState<EquipmentSortKey>('plannedStart');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const { dateFrom, dateTo } = useMemo(
    () => getScheduleTableDateRange(filters.startDate, filters.endDate),
    [filters.startDate, filters.endDate]
  );

  /**
   * Wire form of the multiselect area/work-type filters. `filters` comes from the page's *applied*
   * state, so these arrays keep their identity until Apply — safe to use as effect deps, exactly as
   * `projectIds` / `statuses` already were.
   */
  const serviceAreaParam = toScheduleListParam(filters.serviceAreas);
  const foremanAreaParam = toScheduleListParam(filters.foremanAreas);
  const workTypeParam = toScheduleListParam(filters.workTypes);

  const serviceAreaFilterIds = useMemo(
    () => parseServiceAreaFilterIds(filters.serviceAreas),
    [filters.serviceAreas]
  );

  const foremanById = useMemo(() => {
    const m = new Map<number, string>();
    (Array.isArray(foremanAreas) ? foremanAreas : []).forEach((fa) => m.set(fa.id, fa.name));
    return m;
  }, [foremanAreas]);

  const projectById = useMemo(() => {
    const m = new Map<number, { id: number; name: string; projectNumber: string }>();
    (Array.isArray(projects) ? projects : []).forEach((p) => m.set(p.id, p));
    return m;
  }, [projects]);

  const projectLabel = useCallback(
    (projectId: number | null | undefined) => {
      if (!projectId) return '–';
      const hit = projectById.get(projectId);
      if (!hit) return `#${projectId}`;
      return hit.projectNumber ? `${hit.projectNumber} - ${hit.name}` : hit.name;
    },
    [projectById]
  );

  useEffect(() => {
    setPage(1);
  }, [
    dateFrom,
    dateTo,
    filters.serviceAreas,
    filters.foremanAreas,
    filters.projectIds,
    filters.activityIds,
    filters.activityNumbers,
    filters.statuses,
    filters.equipmentTypes,
    filters.workTypes,
    filters.taskSearch,
    filters.startDate,
    filters.endDate,
  ]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setRawResources(null);

    const resourcesPromise = getScheduleResources({
      dateFrom,
      dateTo,
      serviceArea: serviceAreaParam,
      foremanArea: foremanAreaParam,
      workType: workTypeParam,
      includeUnassignedVehicleTasks: true,
    });
    const vehiclesPromise = getScheduleVehicles({
      serviceArea: serviceAreaParam,
      foremanArea: foremanAreaParam,
    });

    Promise.allSettled([resourcesPromise, vehiclesPromise]).then(([resourcesResult, vehiclesResult]) => {
      if (cancelled) return;
      const vehicleRows =
        vehiclesResult.status === 'fulfilled' && Array.isArray(vehiclesResult.value) ? vehiclesResult.value : [];
      if (vehiclesResult.status === 'rejected') {
        console.error('Schedule equipment table: failed to load vehicles', vehiclesResult.reason);
      }
      if (resourcesResult.status === 'fulfilled') {
        const filtered = filterResourcesByTaskFilters(
          Array.isArray(resourcesResult.value) ? resourcesResult.value : [],
          vehicleRows,
          filters
        );
        const searched = filterResourcesByTaskSearch(filtered, filters.taskSearch);
        const aggregated = buildVehicleResources(searched, vehicleRows, serviceAreaFilterIds);
        setRawResources(aggregated);
        setLoadError(null);
      } else {
        console.error('Schedule equipment table: failed to load resources', resourcesResult.reason);
        setRawResources(null);
        const status = (resourcesResult.reason as { response?: { status?: number } })?.response?.status;
        setLoadError(status === 403 ? 'forbidden' : 'error');
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [
    dateFrom,
    dateTo,
    filters.serviceAreas,
    filters.foremanAreas,
    filters.workTypes,
    filters.equipmentTypes,
    filters.projectIds,
    filters.activityIds,
    filters.activityNumbers,
    filters.statuses,
    filters.taskSearch,
    filters.startDate,
    filters.endDate,
    serviceAreaFilterIds,
    serviceAreaParam,
    foremanAreaParam,
    workTypeParam,
  ]);

  const flatRows: EquipmentRow[] = useMemo(() => {
    const list = Array.isArray(rawResources) ? rawResources : [];
    const out: EquipmentRow[] = [];
    for (const unit of list) {
      const tasks = Array.isArray(unit.tasks) ? unit.tasks : [];
      if (tasks.length === 0) continue;
      const equipmentType = unit.name || '–';
      const serviceAreaCapacity = unit.position || '–';
      for (const task of tasks) {
        const faId = task.foremanAreaId ?? null;
        const foremanAreaName = faId ? foremanById.get(faId) ?? `#${faId}` : '–';
        const plannedStartRaw = String(task.identifiedDate ?? task.startDate ?? '').trim();
        const plannedEndRaw = String(task.plannedCompletionDate ?? '').trim();
        out.push({
          equipmentType,
          serviceAreaCapacity,
          taskId: task.id,
          taskNumber: String(task.taskNumber ?? '').trim() || '–',
          taskName: String(task.name ?? '').trim() || '–',
          projectLabel: projectLabel(task.projectId),
          foremanAreaName,
          plannedStartRaw,
          plannedEndRaw,
          status: String(task.status ?? '').trim() || '–',
          yearRound: Boolean(task.yearRoundSchedule),
        });
      }
    }
    return out;
  }, [rawResources, foremanById, projectLabel]);

  const dirMult = sortDir === 'asc' ? 1 : -1;

  const sortedRows = useMemo(() => {
    const slice = flatRows.slice();
    slice.sort((a, b) => {
      switch (sortKey) {
        case 'plannedStart':
          return compareNullableTime(
            dateStringToSortTime(a.plannedStartRaw),
            dateStringToSortTime(b.plannedStartRaw),
            dirMult
          );
        case 'plannedEnd':
          return compareNullableTime(
            dateStringToSortTime(a.plannedEndRaw),
            dateStringToSortTime(b.plannedEndRaw),
            dirMult
          );
        case 'yearRound':
          return dirMult * (Number(a.yearRound) - Number(b.yearRound));
        case 'equipmentType':
          return compareLocale(a.equipmentType, b.equipmentType, dirMult);
        case 'serviceAreaCapacity':
          return compareLocale(a.serviceAreaCapacity, b.serviceAreaCapacity, dirMult);
        case 'taskNumber':
          return compareLocale(a.taskNumber, b.taskNumber, dirMult);
        case 'taskName':
          return compareLocale(a.taskName, b.taskName, dirMult);
        case 'projectLabel':
          return compareLocale(a.projectLabel, b.projectLabel, dirMult);
        case 'foremanAreaName':
          return compareLocale(a.foremanAreaName, b.foremanAreaName, dirMult);
        case 'status':
          return compareLocale(a.status, b.status, dirMult);
        default:
          return 0;
      }
    });
    return slice;
  }, [flatRows, sortKey, sortDir, dirMult]);

  const totalCount = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const paginatedRows = useMemo(
    () => sortedRows.slice((page - 1) * pageSize, page * pageSize),
    [sortedRows, page, pageSize]
  );

  const toggleSort = (key: EquipmentSortKey) => {
    setSortDir((prev) => (sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  };

  const th = (key: EquipmentSortKey, label: string) => {
    const active = sortKey === key;
    // Standard app sort-arrow set (matches SortableTh / QP getSortIcon).
    const icon = !active ? (
      <i className="bi bi-arrow-down-up tw-ml-1 tw-text-content-muted" aria-hidden="true" />
    ) : sortDir === 'asc' ? (
      <i className="bi bi-sort-down-alt tw-ml-1" aria-hidden="true" />
    ) : (
      <i className="bi bi-sort-down tw-ml-1" aria-hidden="true" />
    );
    return (
      <th
        role="button"
        tabIndex={0}
        aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
        onClick={() => toggleSort(key)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleSort(key);
          }
        }}
      >
        {label}
        {icon}
      </th>
    );
  };

  if (loading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-grow min-h-0 tw-py-5 tw-gap-2">
        <Spinner animation="border" size="sm" />
        <span className="tw-text-content-muted">Loading equipment schedule…</span>
      </div>
    );
  }

  if (loadError === 'forbidden') {
    return (
      <div className="tw-grow min-h-0">
        <Alert variant="warning">You do not have permission to view the schedule.</Alert>
      </div>
    );
  }

  if (loadError === 'error') {
    return (
      <div className="tw-grow min-h-0">
        <Alert variant="danger">Failed to load schedule data. Try again or adjust filters.</Alert>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-grow min-h-0" style={{ minHeight: 0 }}>
      <div className="tw-shrink-0 tw-text-sm tw-text-content-muted tw-mb-2">
        Showing tasks from {dateFrom} to {dateTo}.
      </div>
      {sortedRows.length === 0 ? (
        <div className="tw-text-content-muted tw-py-4 tw-shrink-0">No equipment tasks match the current filters and date range.</div>
      ) : (
        <>
          <div className="tw-grow min-h-0 tw-overflow-auto tw-border tw-border-line tw-rounded tw-pb-3">
            <div className="tw-overflow-x-auto">
              <Table striped bordered hover size="sm" className="tw-mb-0 tw-align-middle">
                <thead className="tw-bg-surface tw-sticky tw-top-0">
                  <tr>
                    {th('equipmentType', 'Equipment Type')}
                    {th('serviceAreaCapacity', 'Service Area / Capacity')}
                    {th('foremanAreaName', 'Foreman Area')}
                    {th('taskNumber', 'Task ID')}
                    {th('taskName', 'Task Name')}
                    {th('projectLabel', 'Project')}
                    {th('plannedStart', 'Planned Start')}
                    {th('plannedEnd', 'Planned End')}
                    {th('status', 'Status')}
                    {th('yearRound', 'Year-Round')}
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.map((r) => (
                    <tr key={`${r.taskId}|${r.equipmentType}|${r.serviceAreaCapacity}`}>
                      <td>{r.equipmentType}</td>
                      <td>{r.serviceAreaCapacity}</td>
                      <td>{r.foremanAreaName}</td>
                      <td>{r.taskNumber}</td>
                      {/* V3-022 / TBL-06: at most 2 lines, full name in the shared tooltip. */}
                      <td><ClampText>{r.taskName}</ClampText></td>
                      <td>{r.projectLabel}</td>
                      <td className="tw-whitespace-nowrap">{formatScheduleDateCell(r.plannedStartRaw)}</td>
                      <td className="tw-whitespace-nowrap">{formatScheduleDateCell(r.plannedEndRaw)}</td>
                      <td>{r.status}</td>
                      <td>{r.yearRound ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
          <div className="tw-shrink-0 tw-pt-2">
            <ServerPagination
              currentPage={page}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ScheduleEquipmentTable;
